import { supabase } from './supabase';

const BUCKET = 'project-media';
const SIGNED_TTL_SECONDS = 3600;

interface CachedUrl { url: string; expiresAt: number; }
const urlCache = new Map<string, CachedUrl>();

export async function signedUrl(path: string): Promise<string> {
  const cached = urlCache.get(path);
  if (cached && cached.expiresAt > Date.now() + 30_000) return cached.url;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_TTL_SECONDS);
  if (error) throw error;
  urlCache.set(path, {
    url: data.signedUrl,
    expiresAt: Date.now() + SIGNED_TTL_SECONDS * 1000,
  });
  return data.signedUrl;
}

export async function uploadProjectMedia(
  file: File,
  projectId: string,
  prefix: 'files' | 'hero' | `rooms/${string}`,
): Promise<{ path: string; mimeType: string; sizeBytes: number }> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
  const path = `${projectId}/${prefix}/${crypto.randomUUID()}-${safeName}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, {
      contentType: file.type || 'application/octet-stream',
      upsert: false,
    });
  if (error) throw error;
  return { path, mimeType: file.type || 'application/octet-stream', sizeBytes: file.size };
}

export async function removeMedia(path: string): Promise<void> {
  urlCache.delete(path);
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) throw error;
}

/** Heuristic: storage paths look like "<uuid>/...", remote URLs start with http(s). */
export async function resolveMediaSrc(pathOrUrl: string | null): Promise<string | null> {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return signedUrl(pathOrUrl);
}

export function fileTypeFromMime(mime: string | null | undefined): 'image' | 'pdf' | 'spreadsheet' | 'doc' | 'cad' | 'other' {
  if (!mime) return 'other';
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.includes('spreadsheet') || mime === 'text/csv' || mime.includes('excel')) return 'spreadsheet';
  if (mime.includes('word') || mime === 'text/plain' || mime.includes('document')) return 'doc';
  if (mime.includes('dwg') || mime.includes('dxf') || mime.includes('autocad')) return 'cad';
  return 'other';
}

export function fmtSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
